// TranslationsDrawer.tsx · v12.10.31 (#2267 a "<System> value" column beside the ID - the other system's own name for the record; record only, never sent) · 2026-11-05 · VoiceSpectrum AI
// TranslationsDrawer.tsx · v12.10.28 (#2209 Integrations › connected system › Translations) · 2026-11-02 · VoiceSpectrum AI
// v12.10.28 (#2209, Tushar 11/2): "in translations we can pick token like location, it will list values, against which we can enter
// external system id. similar way visit type and Proivder type." One drawer per connected system: pick a token, every value of its
// list is listed with this system's id. A call sends that id through {location.externalId} / {provider.externalId} /
// {request.TypeExternalID} when an action of THIS system runs. Blank = not sent.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api, { ApiError } from '../../lib/api';
import '../personalization.css';   // .pz-drawer (the 680px settings drawer)
import './translations.css';

export interface TranslationValue { entryId: string; name: string; active: boolean; externalId: string; externalValue?: string }   // v12.10.31 (#2267): externalValue
export interface TranslationToken { token: string; field: string; label: string; listKey: string; listName: string; managed: boolean; values: TranslationValue[] }
export interface TranslationsPayload { systemId: number; system: string; tokens: TranslationToken[] }

const key = (id: number | string) => ['t2-integration-translations', String(id)] as const;

function errorText(e: unknown): string {
  if (e instanceof ApiError) return e.message || 'Could not save.';
  return e instanceof Error ? e.message : 'Could not save.';
}

export function TranslationsDrawer({ systemId, systemName, canEdit = true, onClose, onSaved }: {
  systemId: number | string; systemName: string; canEdit?: boolean; onClose: () => void; onSaved?: (m: string) => void;
}) {
  const qc = useQueryClient();
  const q = useQuery<TranslationsPayload>({
    queryKey: key(systemId),
    queryFn: () => api.get<TranslationsPayload>(`/t2/settings/integrations/connected-systems/${systemId}/translations`),
  });
  const tokens = q.data?.tokens ?? [];
  const [pick, setPick] = useState<string>('');
  const cur = useMemo(() => tokens.find((t) => t.token === pick) ?? tokens[0], [tokens, pick]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [vals, setVals] = useState<Record<string, string>>({});   // v12.10.31 (#2267): the other system's value per entry
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!cur) return;
    setDraft(Object.fromEntries(cur.values.map((v) => [v.entryId, v.externalId ?? ''])));
    setVals(Object.fromEntries(cur.values.map((v) => [v.entryId, v.externalValue ?? ''])));
    setErr('');
  }, [cur]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const dirty = !!cur && cur.values.some((v) => (draft[v.entryId] ?? '') !== (v.externalId ?? '') || (vals[v.entryId] ?? '') !== (v.externalValue ?? ''));

  const save = useMutation({
    mutationFn: () => api.put<TranslationsPayload>(`/t2/settings/integrations/connected-systems/${systemId}/translations`, {
      token: cur!.token,
      ids: Object.fromEntries(cur!.values.map((v) => [v.entryId, (draft[v.entryId] ?? '').trim()])),
      values: Object.fromEntries(cur!.values.map((v) => [v.entryId, (vals[v.entryId] ?? '').trim()])),   // v12.10.31 (#2267): record only
    }),
    onSuccess: (res) => {
      qc.setQueryData(key(systemId), res);
      qc.invalidateQueries({ queryKey: ['t2-org-vocabulary'] });
      qc.invalidateQueries({ queryKey: ['t2-org-locations'] });
      onSaved?.(`${cur!.label} IDs saved for ${systemName}`);
    },
    onError: (e) => setErr(errorText(e)),
  });

  return (
    <div className="pz-modal-scrim" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pz-drawer tr-drawer" role="dialog" aria-modal="true" aria-label={`Translations · ${systemName}`}>
        <div className="dw-accent" />
        <div className="dw-head">
          <div><h4>Translations · {systemName}</h4><p>What {systemName} calls each value. Sent with an action of {systemName} that uses the token.</p></div>
          <button type="button" className="modal-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="dw-body">
          {q.isLoading && <div className="tr-hint">Loading…</div>}
          {q.isError && <div className="tr-err">Translations could not be loaded.</div>}
          {tokens.length > 0 && (
            <div className="tr-pills" role="tablist" aria-label="Token">
              {tokens.map((t) => (
                <button key={t.token} type="button" role="tab" aria-selected={t.token === cur?.token}
                  className={`tr-pill${t.token === cur?.token ? ' on' : ''}`} onClick={() => setPick(t.token)}>
                  {t.label} <code>{`{${t.token}}`}</code>
                </button>
              ))}
            </div>
          )}
          {cur && (
            <>
              <div className="tr-hint">
                {cur.listKey
                  ? <>Values from the <b>{cur.listName || cur.listKey}</b> list{cur.managed ? <> (filled from Organization › Locations)</> : <> (Vocabulary › Lists)</>}. Rides on <code>{`{${cur.field}}`}</code> - the value the caller chose.</>
                  : <><code>{`{${cur.field}}`}</code> is not tied to a list yet - tie it in Vocabulary › Fields.</>}
              </div>
              {cur.values.length === 0 && cur.listKey && (
                <div className="tr-empty">No values yet. {cur.managed ? 'Add locations in Organization › Locations.' : `Add them in Vocabulary › Lists › ${cur.listName || cur.listKey}.`}</div>
              )}
              {cur.values.length > 0 && (
                <div className="tr-table">
                  <div className="tr-row tr-row3 tr-headrow"><span>Value</span><span>{systemName} ID</span><span title="What the other system calls this record - kept beside the ID for reference, never sent">{systemName} value</span></div>
                  {cur.values.map((v) => (
                    <label key={v.entryId} className={`tr-row tr-row3${v.active ? '' : ' off'}`}>
                      <span className="tr-name">{v.name}{!v.active && <small> (off)</small>}</span>
                      <input value={draft[v.entryId] ?? ''} disabled={!canEdit} placeholder="not set - not sent"
                        onChange={(e) => setDraft((d) => ({ ...d, [v.entryId]: e.target.value }))} />
                      {/* v12.10.31 (#2267): e.g. 161 -> "SERENA LEE-LOWER PARSIPPANY" - record only, not sent */}
                      <input value={vals[v.entryId] ?? ''} disabled={!canEdit} placeholder={`${systemName}'s name (optional)`} aria-label={`${systemName} value for ${v.name}`}
                        onChange={(e) => setVals((d) => ({ ...d, [v.entryId]: e.target.value }))} />
                    </label>
                  ))}
                </div>
              )}
              {err && <div className="tr-err">{err}</div>}
            </>
          )}
        </div>
        <div className="dw-foot">
          <button type="button" className="mc" onClick={onClose}>Close <kbd>Esc</kbd></button>
          {canEdit && <button type="button" className="ma" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save'}</button>}
        </div>
      </div>
    </div>
  );
}
